// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type {
  AuthorListView,
  ContentMetadataCommon,
  ContentId_SUMMARY,
  ContentItemType,
  Country,
  SummaryDocumentType,
  Language,
  KeyQuality,
  SummarySourceType,
  ContentId_ACTIONABLE,
  ContentId_MIAB,
  OnlineContentCreatorGender,
  ContentId_ONLINE_CONTENT,
  OnlineContentFormatType,
  ContentId_EXPERT_VIDEO,
  ContentId_QUICKWIN,
  QuickWinType,
  ContentMetadata,
} from "../schema";
import { serialize_AuthorListView_internal, deserialize_AuthorListView_internal } from "./AuthorListView";
import { serialize_ContentMetadataCommon_internal, deserialize_ContentMetadataCommon_internal } from "./ContentMetadataCommon";
import { serialize_ContentId_SUMMARY_internal, deserialize_ContentId_SUMMARY_internal } from "./ContentId_SUMMARY";
import { serialize_ContentItemType_internal, deserialize_ContentItemType_internal } from "./ContentItemType";
import { serialize_Country_internal, deserialize_Country_internal } from "./Country";
import { serialize_SummaryDocumentType_internal, deserialize_SummaryDocumentType_internal } from "./SummaryDocumentType";
import { serialize_Language_internal, deserialize_Language_internal } from "./Language";
import { serialize_KeyQuality_internal, deserialize_KeyQuality_internal } from "./KeyQuality";
import { serialize_SummarySourceType_internal, deserialize_SummarySourceType_internal } from "./SummarySourceType";
import { serialize_ContentId_ACTIONABLE_internal, deserialize_ContentId_ACTIONABLE_internal } from "./ContentId_ACTIONABLE";
import { serialize_ContentId_MIAB_internal, deserialize_ContentId_MIAB_internal } from "./ContentId_MIAB";
import { serialize_OnlineContentCreatorGender_internal, deserialize_OnlineContentCreatorGender_internal } from "./OnlineContentCreatorGender";
import { serialize_ContentId_ONLINE_CONTENT_internal, deserialize_ContentId_ONLINE_CONTENT_internal } from "./ContentId_ONLINE_CONTENT";
import { serialize_OnlineContentFormatType_internal, deserialize_OnlineContentFormatType_internal } from "./OnlineContentFormatType";
import { serialize_ContentId_EXPERT_VIDEO_internal, deserialize_ContentId_EXPERT_VIDEO_internal } from "./ContentId_EXPERT_VIDEO";
import { serialize_ContentId_QUICKWIN_internal, deserialize_ContentId_QUICKWIN_internal } from "./ContentId_QUICKWIN";
import { serialize_QuickWinType_internal, deserialize_QuickWinType_internal } from "./QuickWinType";

export function serialize_ContentMetadata_internal(value: ContentMetadata): unknown {
  return ((
    value:
      | {
          contentType: "SUMMARY";
          activatedAt: DateTime<true> | null;
          activationTime: DateTime<true> | null;
          active: boolean;
          audioActivatedAt: DateTime<true> | null;
          audioActive: boolean;
          audioDuration: number;
          authorInfo: string | null;
          authors: AuthorListView[];
          backgroundColor: string | null;
          bestseller: boolean;
          businessClassic: boolean;
          canonicalName: string;
          common: ContentMetadataCommon;
          contentId: ContentId_SUMMARY;
          contentItemId: bigint;
          contentItemType: ContentItemType;
          countriesExcluded: Country[];
          countriesIncluded: Country[];
          coverModifiedAt: DateTime<true> | null;
          coverUri: string;
          dataId: number;
          descriptionHtml: string | null;
          documentType: SummaryDocumentType;
          duration: number;
          imageBorder: boolean;
          language: Language;
          miabId: bigint | null;
          modifiedAt: DateTime<true> | null;
          originalTitle: string | null;
          previewAvailable: boolean;
          publicationDate: string | null;
          publisherInfo: string | null;
          publisherName: string | null;
          qualities: KeyQuality[];
          rating: number | null;
          sourceType: SummarySourceType;
          subtitle: string | null;
          summaryPages: number;
          summaryWords: number;
          teaser: string | null;
          title: string | null;
        }
      | {
          contentType: "ACTIONABLE";
          actionableId: bigint;
          actionableSetId: bigint | null;
          activatedAt: DateTime<true> | null;
          active: boolean;
          common: ContentMetadataCommon;
          contentId: ContentId_ACTIONABLE;
          coverUri: string;
          description: string;
          language: Language;
          modifiedAt: DateTime<true>;
          name: string;
          title: string;
        }
      | { contentType: "MIAB"; common: ContentMetadataCommon; contentId: ContentId_MIAB; customerId: number | null; dataId: number; modifiedAt: DateTime<true> | null }
      | {
          contentType: "ONLINE_CONTENT";
          authorsDetail: {
            editorialTeam: boolean;
            gender: OnlineContentCreatorGender;
            name: string;
            rating: number | null;
          }[];
          common: ContentMetadataCommon;
          contentId: ContentId_ONLINE_CONTENT;
          contentProductionResourceId: bigint | null;
          contentRating: number | null;
          deactivatedAt: DateTime<true> | null;
          formatType: OnlineContentFormatType;
          hashId: string;
          modifiedAt: DateTime<true> | null;
          overallRating: number | null;
          publicationDate: { year: number; month: number; day: number };
          readingTime: number;
          resourceUrl: string;
          sourceDetail: {
            logoUri: string | null;
            rating: number | null;
            title: string;
            url: string;
          };
          subtitle: string | null;
        }
      | { contentType: "EXPERT_VIDEO"; authors: AuthorListView[]; common: ContentMetadataCommon; contentId: ContentId_EXPERT_VIDEO; length: number | null; modifiedAt: DateTime<true>; whatYouWillLearn: string }
      | { contentType: "QUICKWIN"; common: ContentMetadataCommon; contentId: ContentId_QUICKWIN; dataId: number; modifiedAt: DateTime<true>; quickWinType: QuickWinType },
  ): unknown => {
    switch (value.contentType) {
      case "SUMMARY": {
        return {
          contentType: "SUMMARY",
          activatedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["activatedAt"]),
          activationTime: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["activationTime"]),
          active: ((value: boolean): unknown => {
            return value;
          })(value["active"]),
          audioActivatedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["audioActivatedAt"]),
          audioActive: ((value: boolean): unknown => {
            return value;
          })(value["audioActive"]),
          audioDuration: ((value: number): unknown => {
            return value;
          })(value["audioDuration"]),
          authorInfo: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["authorInfo"]),
          authors: ((value: AuthorListView[]): unknown => {
            return value.map((value: AuthorListView): unknown => {
              return serialize_AuthorListView_internal(value);
            });
          })(value["authors"]),
          backgroundColor: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["backgroundColor"]),
          bestseller: ((value: boolean): unknown => {
            return value;
          })(value["bestseller"]),
          businessClassic: ((value: boolean): unknown => {
            return value;
          })(value["businessClassic"]),
          canonicalName: ((value: string): unknown => {
            return value;
          })(value["canonicalName"]),
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_SUMMARY): unknown => {
            return serialize_ContentId_SUMMARY_internal(value);
          })(value["contentId"]),
          contentItemId: ((value: bigint): unknown => {
            return value;
          })(value["contentItemId"]),
          contentItemType: ((value: ContentItemType): unknown => {
            return serialize_ContentItemType_internal(value);
          })(value["contentItemType"]),
          countriesExcluded: ((value: Country[]): unknown => {
            return value.map((value: Country): unknown => {
              return serialize_Country_internal(value);
            });
          })(value["countriesExcluded"]),
          countriesIncluded: ((value: Country[]): unknown => {
            return value.map((value: Country): unknown => {
              return serialize_Country_internal(value);
            });
          })(value["countriesIncluded"]),
          coverModifiedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["coverModifiedAt"]),
          coverUri: ((value: string): unknown => {
            return value;
          })(value["coverUri"]),
          dataId: ((value: number): unknown => {
            return value;
          })(value["dataId"]),
          descriptionHtml: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["descriptionHtml"]),
          documentType: ((value: SummaryDocumentType): unknown => {
            return serialize_SummaryDocumentType_internal(value);
          })(value["documentType"]),
          duration: ((value: number): unknown => {
            return value;
          })(value["duration"]),
          imageBorder: ((value: boolean): unknown => {
            return value;
          })(value["imageBorder"]),
          language: ((value: Language): unknown => {
            return serialize_Language_internal(value);
          })(value["language"]),
          miabId: ((value: bigint | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["miabId"]),
          modifiedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["modifiedAt"]),
          originalTitle: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["originalTitle"]),
          previewAvailable: ((value: boolean): unknown => {
            return value;
          })(value["previewAvailable"]),
          publicationDate: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["publicationDate"]),
          publisherInfo: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["publisherInfo"]),
          publisherName: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["publisherName"]),
          qualities: ((value: KeyQuality[]): unknown => {
            return value.map((value: KeyQuality): unknown => {
              return serialize_KeyQuality_internal(value);
            });
          })(value["qualities"]),
          rating: ((value: number | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["rating"]),
          sourceType: ((value: SummarySourceType): unknown => {
            return serialize_SummarySourceType_internal(value);
          })(value["sourceType"]),
          subtitle: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["subtitle"]),
          summaryPages: ((value: number): unknown => {
            return value;
          })(value["summaryPages"]),
          summaryWords: ((value: number): unknown => {
            return value;
          })(value["summaryWords"]),
          teaser: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["teaser"]),
          title: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["title"]),
        };
      }
      case "ACTIONABLE": {
        return {
          contentType: "ACTIONABLE",
          actionableId: ((value: bigint): unknown => {
            return value;
          })(value["actionableId"]),
          actionableSetId: ((value: bigint | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["actionableSetId"]),
          activatedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["activatedAt"]),
          active: ((value: boolean): unknown => {
            return value;
          })(value["active"]),
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_ACTIONABLE): unknown => {
            return serialize_ContentId_ACTIONABLE_internal(value);
          })(value["contentId"]),
          coverUri: ((value: string): unknown => {
            return value;
          })(value["coverUri"]),
          description: ((value: string): unknown => {
            return value;
          })(value["description"]),
          language: ((value: Language): unknown => {
            return serialize_Language_internal(value);
          })(value["language"]),
          modifiedAt: ((value: DateTime<true>): unknown => {
            return value.toMillis();
          })(value["modifiedAt"]),
          name: ((value: string): unknown => {
            return value;
          })(value["name"]),
          title: ((value: string): unknown => {
            return value;
          })(value["title"]),
        };
      }
      case "MIAB": {
        return {
          contentType: "MIAB",
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_MIAB): unknown => {
            return serialize_ContentId_MIAB_internal(value);
          })(value["contentId"]),
          customerId: ((value: number | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["customerId"]),
          dataId: ((value: number): unknown => {
            return value;
          })(value["dataId"]),
          modifiedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toMillis();
          })(value["modifiedAt"]),
        };
      }
      case "ONLINE_CONTENT": {
        return {
          contentType: "ONLINE_CONTENT",
          authorsDetail: ((
            value: {
              editorialTeam: boolean;
              gender: OnlineContentCreatorGender;
              name: string;
              rating: number | null;
            }[],
          ): unknown => {
            return value.map((value: { editorialTeam: boolean; gender: OnlineContentCreatorGender; name: string; rating: number | null }): unknown => {
              return {
                editorialTeam: ((value: boolean): unknown => {
                  return value;
                })(value["editorialTeam"]),
                gender: ((value: OnlineContentCreatorGender): unknown => {
                  return serialize_OnlineContentCreatorGender_internal(value);
                })(value["gender"]),
                name: ((value: string): unknown => {
                  return value;
                })(value["name"]),
                rating: ((value: number | null): unknown => {
                  if (value === null) {
                    return null;
                  }
                  return value;
                })(value["rating"]),
              };
            });
          })(value["authorsDetail"]),
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_ONLINE_CONTENT): unknown => {
            return serialize_ContentId_ONLINE_CONTENT_internal(value);
          })(value["contentId"]),
          contentProductionResourceId: ((value: bigint | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["contentProductionResourceId"]),
          contentRating: ((value: number | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["contentRating"]),
          deactivatedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toISO();
          })(value["deactivatedAt"]),
          formatType: ((value: OnlineContentFormatType): unknown => {
            return serialize_OnlineContentFormatType_internal(value);
          })(value["formatType"]),
          hashId: ((value: string): unknown => {
            return value;
          })(value["hashId"]),
          modifiedAt: ((value: DateTime<true> | null): unknown => {
            if (value === null) {
              return null;
            }
            return value.toISO();
          })(value["modifiedAt"]),
          overallRating: ((value: number | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["overallRating"]),
          publicationDate: ((value: { year: number; month: number; day: number }): unknown => {
            return `${value.year.toFixed().padStart(4, "0")}-${value.month.toFixed().padStart(2, "0")}-${value.day.toFixed().padStart(2, "0")}`;
          })(value["publicationDate"]),
          readingTime: ((value: number): unknown => {
            return value;
          })(value["readingTime"]),
          resourceUrl: ((value: string): unknown => {
            return value;
          })(value["resourceUrl"]),
          sourceDetail: ((value: { logoUri: string | null; rating: number | null; title: string; url: string }): unknown => {
            return {
              logoUri: ((value: string | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["logoUri"]),
              rating: ((value: number | null): unknown => {
                if (value === null) {
                  return null;
                }
                return value;
              })(value["rating"]),
              title: ((value: string): unknown => {
                return value;
              })(value["title"]),
              url: ((value: string): unknown => {
                return value;
              })(value["url"]),
            };
          })(value["sourceDetail"]),
          subtitle: ((value: string | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["subtitle"]),
        };
      }
      case "EXPERT_VIDEO": {
        return {
          contentType: "EXPERT_VIDEO",
          authors: ((value: AuthorListView[]): unknown => {
            return value.map((value: AuthorListView): unknown => {
              return serialize_AuthorListView_internal(value);
            });
          })(value["authors"]),
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_EXPERT_VIDEO): unknown => {
            return serialize_ContentId_EXPERT_VIDEO_internal(value);
          })(value["contentId"]),
          length: ((value: number | null): unknown => {
            if (value === null) {
              return null;
            }
            return value;
          })(value["length"]),
          modifiedAt: ((value: DateTime<true>): unknown => {
            return value.toMillis();
          })(value["modifiedAt"]),
          whatYouWillLearn: ((value: string): unknown => {
            return value;
          })(value["whatYouWillLearn"]),
        };
      }
      case "QUICKWIN": {
        return {
          contentType: "QUICKWIN",
          common: ((value: ContentMetadataCommon): unknown => {
            return serialize_ContentMetadataCommon_internal(value);
          })(value["common"]),
          contentId: ((value: ContentId_QUICKWIN): unknown => {
            return serialize_ContentId_QUICKWIN_internal(value);
          })(value["contentId"]),
          dataId: ((value: number): unknown => {
            return value;
          })(value["dataId"]),
          modifiedAt: ((value: DateTime<true>): unknown => {
            return value.toMillis();
          })(value["modifiedAt"]),
          quickWinType: ((value: QuickWinType): unknown => {
            return serialize_QuickWinType_internal(value);
          })(value["quickWinType"]),
        };
      }
    }
  })(value);
}
export function serialize_ContentMetadata(obj: ContentMetadata): string {
  return renderJsonWithNumbersAsBigint(serialize_ContentMetadata_internal(obj));
}
export function deserialize_ContentMetadata_internal(value: unknown): ParserResult<ContentMetadata> {
  return ((
    value: unknown,
  ): ParserResult<
    | {
        contentType: "SUMMARY";
        activatedAt: DateTime<true> | null;
        activationTime: DateTime<true> | null;
        active: boolean;
        audioActivatedAt: DateTime<true> | null;
        audioActive: boolean;
        audioDuration: number;
        authorInfo: string | null;
        authors: AuthorListView[];
        backgroundColor: string | null;
        bestseller: boolean;
        businessClassic: boolean;
        canonicalName: string;
        common: ContentMetadataCommon;
        contentId: ContentId_SUMMARY;
        contentItemId: bigint;
        contentItemType: ContentItemType;
        countriesExcluded: Country[];
        countriesIncluded: Country[];
        coverModifiedAt: DateTime<true> | null;
        coverUri: string;
        dataId: number;
        descriptionHtml: string | null;
        documentType: SummaryDocumentType;
        duration: number;
        imageBorder: boolean;
        language: Language;
        miabId: bigint | null;
        modifiedAt: DateTime<true> | null;
        originalTitle: string | null;
        previewAvailable: boolean;
        publicationDate: string | null;
        publisherInfo: string | null;
        publisherName: string | null;
        qualities: KeyQuality[];
        rating: number | null;
        sourceType: SummarySourceType;
        subtitle: string | null;
        summaryPages: number;
        summaryWords: number;
        teaser: string | null;
        title: string | null;
      }
    | {
        contentType: "ACTIONABLE";
        actionableId: bigint;
        actionableSetId: bigint | null;
        activatedAt: DateTime<true> | null;
        active: boolean;
        common: ContentMetadataCommon;
        contentId: ContentId_ACTIONABLE;
        coverUri: string;
        description: string;
        language: Language;
        modifiedAt: DateTime<true>;
        name: string;
        title: string;
      }
    | { contentType: "MIAB"; common: ContentMetadataCommon; contentId: ContentId_MIAB; customerId: number | null; dataId: number; modifiedAt: DateTime<true> | null }
    | {
        contentType: "ONLINE_CONTENT";
        authorsDetail: {
          editorialTeam: boolean;
          gender: OnlineContentCreatorGender;
          name: string;
          rating: number | null;
        }[];
        common: ContentMetadataCommon;
        contentId: ContentId_ONLINE_CONTENT;
        contentProductionResourceId: bigint | null;
        contentRating: number | null;
        deactivatedAt: DateTime<true> | null;
        formatType: OnlineContentFormatType;
        hashId: string;
        modifiedAt: DateTime<true> | null;
        overallRating: number | null;
        publicationDate: { year: number; month: number; day: number };
        readingTime: number;
        resourceUrl: string;
        sourceDetail: {
          logoUri: string | null;
          rating: number | null;
          title: string;
          url: string;
        };
        subtitle: string | null;
      }
    | { contentType: "EXPERT_VIDEO"; authors: AuthorListView[]; common: ContentMetadataCommon; contentId: ContentId_EXPERT_VIDEO; length: number | null; modifiedAt: DateTime<true>; whatYouWillLearn: string }
    | { contentType: "QUICKWIN"; common: ContentMetadataCommon; contentId: ContentId_QUICKWIN; dataId: number; modifiedAt: DateTime<true>; quickWinType: QuickWinType }
  > => {
    return ((
      value: unknown,
    ): ParserResult<
      | {
          contentType: "SUMMARY";
          activatedAt: DateTime<true> | null;
          activationTime: DateTime<true> | null;
          active: boolean;
          audioActivatedAt: DateTime<true> | null;
          audioActive: boolean;
          audioDuration: number;
          authorInfo: string | null;
          authors: AuthorListView[];
          backgroundColor: string | null;
          bestseller: boolean;
          businessClassic: boolean;
          canonicalName: string;
          common: ContentMetadataCommon;
          contentId: ContentId_SUMMARY;
          contentItemId: bigint;
          contentItemType: ContentItemType;
          countriesExcluded: Country[];
          countriesIncluded: Country[];
          coverModifiedAt: DateTime<true> | null;
          coverUri: string;
          dataId: number;
          descriptionHtml: string | null;
          documentType: SummaryDocumentType;
          duration: number;
          imageBorder: boolean;
          language: Language;
          miabId: bigint | null;
          modifiedAt: DateTime<true> | null;
          originalTitle: string | null;
          previewAvailable: boolean;
          publicationDate: string | null;
          publisherInfo: string | null;
          publisherName: string | null;
          qualities: KeyQuality[];
          rating: number | null;
          sourceType: SummarySourceType;
          subtitle: string | null;
          summaryPages: number;
          summaryWords: number;
          teaser: string | null;
          title: string | null;
        }
      | {
          contentType: "ACTIONABLE";
          actionableId: bigint;
          actionableSetId: bigint | null;
          activatedAt: DateTime<true> | null;
          active: boolean;
          common: ContentMetadataCommon;
          contentId: ContentId_ACTIONABLE;
          coverUri: string;
          description: string;
          language: Language;
          modifiedAt: DateTime<true>;
          name: string;
          title: string;
        }
      | { contentType: "MIAB"; common: ContentMetadataCommon; contentId: ContentId_MIAB; customerId: number | null; dataId: number; modifiedAt: DateTime<true> | null }
      | {
          contentType: "ONLINE_CONTENT";
          authorsDetail: {
            editorialTeam: boolean;
            gender: OnlineContentCreatorGender;
            name: string;
            rating: number | null;
          }[];
          common: ContentMetadataCommon;
          contentId: ContentId_ONLINE_CONTENT;
          contentProductionResourceId: bigint | null;
          contentRating: number | null;
          deactivatedAt: DateTime<true> | null;
          formatType: OnlineContentFormatType;
          hashId: string;
          modifiedAt: DateTime<true> | null;
          overallRating: number | null;
          publicationDate: { year: number; month: number; day: number };
          readingTime: number;
          resourceUrl: string;
          sourceDetail: {
            logoUri: string | null;
            rating: number | null;
            title: string;
            url: string;
          };
          subtitle: string | null;
        }
      | { contentType: "EXPERT_VIDEO"; authors: AuthorListView[]; common: ContentMetadataCommon; contentId: ContentId_EXPERT_VIDEO; length: number | null; modifiedAt: DateTime<true>; whatYouWillLearn: string }
      | { contentType: "QUICKWIN"; common: ContentMetadataCommon; contentId: ContentId_QUICKWIN; dataId: number; modifiedAt: DateTime<true>; quickWinType: QuickWinType }
    > => {
      if (value == null) {
        return { success: false, reason: `[#/components/schemas/ContentMetadata]: '${value}' expected to be non-null.` };
      }
      if (typeof value != "object") {
        return { success: false, reason: `[#/components/schemas/ContentMetadata]: ${value} expected to be of type 'object'.` };
      }
      if (!hasProp(value, "contentType")) {
        return { success: false, reason: `[#/components/schemas/ContentMetadata]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentType'.` };
      }
      if (typeof value["contentType"] != "string") {
        return { success: false, reason: `[#/components/schemas/ContentMetadata]: '${value["contentType"]}' expected to be of type 'string'.` };
      }
      const discriminator = value["contentType"];
      switch (discriminator) {
        case "SUMMARY": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            activatedAt: DateTime<true> | null;
            activationTime: DateTime<true> | null;
            active: boolean;
            audioActivatedAt: DateTime<true> | null;
            audioActive: boolean;
            audioDuration: number;
            authorInfo: string | null;
            authors: AuthorListView[];
            backgroundColor: string | null;
            bestseller: boolean;
            businessClassic: boolean;
            canonicalName: string;
            common: ContentMetadataCommon;
            contentId: ContentId_SUMMARY;
            contentItemId: bigint;
            contentItemType: ContentItemType;
            countriesExcluded: Country[];
            countriesIncluded: Country[];
            coverModifiedAt: DateTime<true> | null;
            coverUri: string;
            dataId: number;
            descriptionHtml: string | null;
            documentType: SummaryDocumentType;
            duration: number;
            imageBorder: boolean;
            language: Language;
            miabId: bigint | null;
            modifiedAt: DateTime<true> | null;
            originalTitle: string | null;
            previewAvailable: boolean;
            publicationDate: string | null;
            publisherInfo: string | null;
            publisherName: string | null;
            qualities: KeyQuality[];
            rating: number | null;
            sourceType: SummarySourceType;
            subtitle: string | null;
            summaryPages: number;
            summaryWords: number;
            teaser: string | null;
            title: string | null;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "activatedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activatedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activatedAt'.` };
            }
            const parsed_activatedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activatedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activatedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["activatedAt"]);
            })();
            if (!parsed_activatedAt.success) {
              return { success: false, reason: parsed_activatedAt.reason };
            }
            if (!hasProp(value, "activationTime")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activationTime]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activationTime'.` };
            }
            const parsed_activationTime = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activationTime]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/activationTime]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["activationTime"]);
            })();
            if (!parsed_activationTime.success) {
              return { success: false, reason: parsed_activationTime.reason };
            }
            if (!hasProp(value, "active")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/active]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'active'.` };
            }
            const parsed_active = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/active]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["active"]);
            })();
            if (!parsed_active.success) {
              return { success: false, reason: parsed_active.reason };
            }
            if (!hasProp(value, "audioActivatedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioActivatedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'audioActivatedAt'.` };
            }
            const parsed_audioActivatedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioActivatedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioActivatedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["audioActivatedAt"]);
            })();
            if (!parsed_audioActivatedAt.success) {
              return { success: false, reason: parsed_audioActivatedAt.reason };
            }
            if (!hasProp(value, "audioActive")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioActive]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'audioActive'.` };
            }
            const parsed_audioActive = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioActive]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["audioActive"]);
            })();
            if (!parsed_audioActive.success) {
              return { success: false, reason: parsed_audioActive.reason };
            }
            if (!hasProp(value, "audioDuration")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioDuration]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'audioDuration'.` };
            }
            const parsed_audioDuration = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/audioDuration]: '${value}' expected to be of type number.` };
              })(value["audioDuration"]);
            })();
            if (!parsed_audioDuration.success) {
              return { success: false, reason: parsed_audioDuration.reason };
            }
            if (!hasProp(value, "authorInfo")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/authorInfo]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'authorInfo'.` };
            }
            const parsed_authorInfo = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/authorInfo]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["authorInfo"]);
            })();
            if (!parsed_authorInfo.success) {
              return { success: false, reason: parsed_authorInfo.reason };
            }
            if (!hasProp(value, "authors")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/authors]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'authors'.` };
            }
            const parsed_authors = ((): ParserResult<AuthorListView[]> => {
              return ((value: unknown): ParserResult<AuthorListView[]> => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/authors]: '${value}' expected to be an array.` };
                }
                const parsed = value.map((value: unknown): ParserResult<AuthorListView> => {
                  return deserialize_AuthorListView_internal(value);
                });
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/SUMMARY/authors]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["authors"]);
            })();
            if (!parsed_authors.success) {
              return { success: false, reason: parsed_authors.reason };
            }
            if (!hasProp(value, "backgroundColor")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/backgroundColor]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'backgroundColor'.` };
            }
            const parsed_backgroundColor = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/backgroundColor]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["backgroundColor"]);
            })();
            if (!parsed_backgroundColor.success) {
              return { success: false, reason: parsed_backgroundColor.reason };
            }
            if (!hasProp(value, "bestseller")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/bestseller]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'bestseller'.` };
            }
            const parsed_bestseller = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/bestseller]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["bestseller"]);
            })();
            if (!parsed_bestseller.success) {
              return { success: false, reason: parsed_bestseller.reason };
            }
            if (!hasProp(value, "businessClassic")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/businessClassic]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'businessClassic'.` };
            }
            const parsed_businessClassic = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/businessClassic]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["businessClassic"]);
            })();
            if (!parsed_businessClassic.success) {
              return { success: false, reason: parsed_businessClassic.reason };
            }
            if (!hasProp(value, "canonicalName")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/canonicalName]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'canonicalName'.` };
            }
            const parsed_canonicalName = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/canonicalName]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["canonicalName"]);
            })();
            if (!parsed_canonicalName.success) {
              return { success: false, reason: parsed_canonicalName.reason };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_SUMMARY> => {
              return ((value: unknown): ParserResult<ContentId_SUMMARY> => {
                return deserialize_ContentId_SUMMARY_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "contentItemId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/contentItemId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentItemId'.` };
            }
            const parsed_contentItemId = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/contentItemId]: '${value}' expected to be of type bigint or number.` };
              })(value["contentItemId"]);
            })();
            if (!parsed_contentItemId.success) {
              return { success: false, reason: parsed_contentItemId.reason };
            }
            if (!hasProp(value, "contentItemType")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/contentItemType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentItemType'.` };
            }
            const parsed_contentItemType = ((): ParserResult<ContentItemType> => {
              return ((value: unknown): ParserResult<ContentItemType> => {
                return deserialize_ContentItemType_internal(value);
              })(value["contentItemType"]);
            })();
            if (!parsed_contentItemType.success) {
              return { success: false, reason: parsed_contentItemType.reason };
            }
            if (!hasProp(value, "countriesExcluded")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesExcluded]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'countriesExcluded'.` };
            }
            const parsed_countriesExcluded = ((): ParserResult<Country[]> => {
              return ((value: unknown): ParserResult<Country[]> => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesExcluded]: '${value}' expected to be an array.` };
                }
                const parsed = value.map((value: unknown): ParserResult<Country> => {
                  return deserialize_Country_internal(value);
                });
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesExcluded]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["countriesExcluded"]);
            })();
            if (!parsed_countriesExcluded.success) {
              return { success: false, reason: parsed_countriesExcluded.reason };
            }
            if (!hasProp(value, "countriesIncluded")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesIncluded]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'countriesIncluded'.` };
            }
            const parsed_countriesIncluded = ((): ParserResult<Country[]> => {
              return ((value: unknown): ParserResult<Country[]> => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesIncluded]: '${value}' expected to be an array.` };
                }
                const parsed = value.map((value: unknown): ParserResult<Country> => {
                  return deserialize_Country_internal(value);
                });
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/SUMMARY/countriesIncluded]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["countriesIncluded"]);
            })();
            if (!parsed_countriesIncluded.success) {
              return { success: false, reason: parsed_countriesIncluded.reason };
            }
            if (!hasProp(value, "coverModifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/coverModifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'coverModifiedAt'.` };
            }
            const parsed_coverModifiedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/coverModifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/coverModifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["coverModifiedAt"]);
            })();
            if (!parsed_coverModifiedAt.success) {
              return { success: false, reason: parsed_coverModifiedAt.reason };
            }
            if (!hasProp(value, "coverUri")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/coverUri]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'coverUri'.` };
            }
            const parsed_coverUri = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/coverUri]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["coverUri"]);
            })();
            if (!parsed_coverUri.success) {
              return { success: false, reason: parsed_coverUri.reason };
            }
            if (!hasProp(value, "dataId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/dataId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'dataId'.` };
            }
            const parsed_dataId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/dataId]: '${value}' expected to be of type number.` };
              })(value["dataId"]);
            })();
            if (!parsed_dataId.success) {
              return { success: false, reason: parsed_dataId.reason };
            }
            if (!hasProp(value, "descriptionHtml")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/descriptionHtml]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'descriptionHtml'.` };
            }
            const parsed_descriptionHtml = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/descriptionHtml]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["descriptionHtml"]);
            })();
            if (!parsed_descriptionHtml.success) {
              return { success: false, reason: parsed_descriptionHtml.reason };
            }
            if (!hasProp(value, "documentType")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/documentType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'documentType'.` };
            }
            const parsed_documentType = ((): ParserResult<SummaryDocumentType> => {
              return ((value: unknown): ParserResult<SummaryDocumentType> => {
                return deserialize_SummaryDocumentType_internal(value);
              })(value["documentType"]);
            })();
            if (!parsed_documentType.success) {
              return { success: false, reason: parsed_documentType.reason };
            }
            if (!hasProp(value, "duration")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/duration]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'duration'.` };
            }
            const parsed_duration = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/duration]: '${value}' expected to be of type number.` };
              })(value["duration"]);
            })();
            if (!parsed_duration.success) {
              return { success: false, reason: parsed_duration.reason };
            }
            if (!hasProp(value, "imageBorder")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/imageBorder]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'imageBorder'.` };
            }
            const parsed_imageBorder = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/imageBorder]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["imageBorder"]);
            })();
            if (!parsed_imageBorder.success) {
              return { success: false, reason: parsed_imageBorder.reason };
            }
            if (!hasProp(value, "language")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/language]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'language'.` };
            }
            const parsed_language = ((): ParserResult<Language> => {
              return ((value: unknown): ParserResult<Language> => {
                return deserialize_Language_internal(value);
              })(value["language"]);
            })();
            if (!parsed_language.success) {
              return { success: false, reason: parsed_language.reason };
            }
            if (!hasProp(value, "miabId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/miabId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'miabId'.` };
            }
            const parsed_miabId = ((): ParserResult<bigint | null> => {
              return ((value: unknown): ParserResult<bigint | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/miabId]: '${value}' expected to be of type bigint or number.` };
              })(value["miabId"]);
            })();
            if (!parsed_miabId.success) {
              return { success: false, reason: parsed_miabId.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/modifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/modifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            if (!hasProp(value, "originalTitle")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/originalTitle]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'originalTitle'.` };
            }
            const parsed_originalTitle = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/originalTitle]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["originalTitle"]);
            })();
            if (!parsed_originalTitle.success) {
              return { success: false, reason: parsed_originalTitle.reason };
            }
            if (!hasProp(value, "previewAvailable")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/previewAvailable]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'previewAvailable'.` };
            }
            const parsed_previewAvailable = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/previewAvailable]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["previewAvailable"]);
            })();
            if (!parsed_previewAvailable.success) {
              return { success: false, reason: parsed_previewAvailable.reason };
            }
            if (!hasProp(value, "publicationDate")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publicationDate]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'publicationDate'.` };
            }
            const parsed_publicationDate = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publicationDate]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["publicationDate"]);
            })();
            if (!parsed_publicationDate.success) {
              return { success: false, reason: parsed_publicationDate.reason };
            }
            if (!hasProp(value, "publisherInfo")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publisherInfo]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'publisherInfo'.` };
            }
            const parsed_publisherInfo = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publisherInfo]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["publisherInfo"]);
            })();
            if (!parsed_publisherInfo.success) {
              return { success: false, reason: parsed_publisherInfo.reason };
            }
            if (!hasProp(value, "publisherName")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publisherName]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'publisherName'.` };
            }
            const parsed_publisherName = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/publisherName]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["publisherName"]);
            })();
            if (!parsed_publisherName.success) {
              return { success: false, reason: parsed_publisherName.reason };
            }
            if (!hasProp(value, "qualities")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/qualities]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'qualities'.` };
            }
            const parsed_qualities = ((): ParserResult<KeyQuality[]> => {
              return ((value: unknown): ParserResult<KeyQuality[]> => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/qualities]: '${value}' expected to be an array.` };
                }
                const parsed = value.map((value: unknown): ParserResult<KeyQuality> => {
                  return deserialize_KeyQuality_internal(value);
                });
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/SUMMARY/qualities]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["qualities"]);
            })();
            if (!parsed_qualities.success) {
              return { success: false, reason: parsed_qualities.reason };
            }
            if (!hasProp(value, "rating")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/rating]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'rating'.` };
            }
            const parsed_rating = ((): ParserResult<number | null> => {
              return ((value: unknown): ParserResult<number | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/rating]: '${value}' expected to be of type number.` };
              })(value["rating"]);
            })();
            if (!parsed_rating.success) {
              return { success: false, reason: parsed_rating.reason };
            }
            if (!hasProp(value, "sourceType")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/sourceType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'sourceType'.` };
            }
            const parsed_sourceType = ((): ParserResult<SummarySourceType> => {
              return ((value: unknown): ParserResult<SummarySourceType> => {
                return deserialize_SummarySourceType_internal(value);
              })(value["sourceType"]);
            })();
            if (!parsed_sourceType.success) {
              return { success: false, reason: parsed_sourceType.reason };
            }
            if (!hasProp(value, "subtitle")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/subtitle]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'subtitle'.` };
            }
            const parsed_subtitle = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/subtitle]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["subtitle"]);
            })();
            if (!parsed_subtitle.success) {
              return { success: false, reason: parsed_subtitle.reason };
            }
            if (!hasProp(value, "summaryPages")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/summaryPages]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'summaryPages'.` };
            }
            const parsed_summaryPages = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/summaryPages]: '${value}' expected to be of type number.` };
              })(value["summaryPages"]);
            })();
            if (!parsed_summaryPages.success) {
              return { success: false, reason: parsed_summaryPages.reason };
            }
            if (!hasProp(value, "summaryWords")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/summaryWords]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'summaryWords'.` };
            }
            const parsed_summaryWords = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/summaryWords]: '${value}' expected to be of type number.` };
              })(value["summaryWords"]);
            })();
            if (!parsed_summaryWords.success) {
              return { success: false, reason: parsed_summaryWords.reason };
            }
            if (!hasProp(value, "teaser")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/teaser]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'teaser'.` };
            }
            const parsed_teaser = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/teaser]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["teaser"]);
            })();
            if (!parsed_teaser.success) {
              return { success: false, reason: parsed_teaser.reason };
            }
            if (!hasProp(value, "title")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/title]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'title'.` };
            }
            const parsed_title = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/SUMMARY/title]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["title"]);
            })();
            if (!parsed_title.success) {
              return { success: false, reason: parsed_title.reason };
            }
            return {
              success: true,
              value: {
                activatedAt: parsed_activatedAt.value,
                activationTime: parsed_activationTime.value,
                active: parsed_active.value,
                audioActivatedAt: parsed_audioActivatedAt.value,
                audioActive: parsed_audioActive.value,
                audioDuration: parsed_audioDuration.value,
                authorInfo: parsed_authorInfo.value,
                authors: parsed_authors.value,
                backgroundColor: parsed_backgroundColor.value,
                bestseller: parsed_bestseller.value,
                businessClassic: parsed_businessClassic.value,
                canonicalName: parsed_canonicalName.value,
                common: parsed_common.value,
                contentId: parsed_contentId.value,
                contentItemId: parsed_contentItemId.value,
                contentItemType: parsed_contentItemType.value,
                countriesExcluded: parsed_countriesExcluded.value,
                countriesIncluded: parsed_countriesIncluded.value,
                coverModifiedAt: parsed_coverModifiedAt.value,
                coverUri: parsed_coverUri.value,
                dataId: parsed_dataId.value,
                descriptionHtml: parsed_descriptionHtml.value,
                documentType: parsed_documentType.value,
                duration: parsed_duration.value,
                imageBorder: parsed_imageBorder.value,
                language: parsed_language.value,
                miabId: parsed_miabId.value,
                modifiedAt: parsed_modifiedAt.value,
                originalTitle: parsed_originalTitle.value,
                previewAvailable: parsed_previewAvailable.value,
                publicationDate: parsed_publicationDate.value,
                publisherInfo: parsed_publisherInfo.value,
                publisherName: parsed_publisherName.value,
                qualities: parsed_qualities.value,
                rating: parsed_rating.value,
                sourceType: parsed_sourceType.value,
                subtitle: parsed_subtitle.value,
                summaryPages: parsed_summaryPages.value,
                summaryWords: parsed_summaryWords.value,
                teaser: parsed_teaser.value,
                title: parsed_title.value,
              },
            };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "SUMMARY" as const, ...parsedVariant.value } };
        }
        case "ACTIONABLE": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            actionableId: bigint;
            actionableSetId: bigint | null;
            activatedAt: DateTime<true> | null;
            active: boolean;
            common: ContentMetadataCommon;
            contentId: ContentId_ACTIONABLE;
            coverUri: string;
            description: string;
            language: Language;
            modifiedAt: DateTime<true>;
            name: string;
            title: string;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "actionableId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/actionableId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'actionableId'.` };
            }
            const parsed_actionableId = ((): ParserResult<bigint> => {
              return ((value: unknown): ParserResult<bigint> => {
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/actionableId]: '${value}' expected to be of type bigint or number.` };
              })(value["actionableId"]);
            })();
            if (!parsed_actionableId.success) {
              return { success: false, reason: parsed_actionableId.reason };
            }
            if (!hasProp(value, "actionableSetId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/actionableSetId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'actionableSetId'.` };
            }
            const parsed_actionableSetId = ((): ParserResult<bigint | null> => {
              return ((value: unknown): ParserResult<bigint | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/actionableSetId]: '${value}' expected to be of type bigint or number.` };
              })(value["actionableSetId"]);
            })();
            if (!parsed_actionableSetId.success) {
              return { success: false, reason: parsed_actionableSetId.reason };
            }
            if (!hasProp(value, "activatedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/activatedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activatedAt'.` };
            }
            const parsed_activatedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/activatedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/activatedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["activatedAt"]);
            })();
            if (!parsed_activatedAt.success) {
              return { success: false, reason: parsed_activatedAt.reason };
            }
            if (!hasProp(value, "active")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/active]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'active'.` };
            }
            const parsed_active = ((): ParserResult<boolean> => {
              return ((value: unknown): ParserResult<boolean> => {
                if (typeof value !== "boolean") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/active]: '${value}' expected to be of type boolean.` };
                }
                return { success: true, value: value };
              })(value["active"]);
            })();
            if (!parsed_active.success) {
              return { success: false, reason: parsed_active.reason };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_ACTIONABLE> => {
              return ((value: unknown): ParserResult<ContentId_ACTIONABLE> => {
                return deserialize_ContentId_ACTIONABLE_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "coverUri")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/coverUri]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'coverUri'.` };
            }
            const parsed_coverUri = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/coverUri]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["coverUri"]);
            })();
            if (!parsed_coverUri.success) {
              return { success: false, reason: parsed_coverUri.reason };
            }
            if (!hasProp(value, "description")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/description]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'description'.` };
            }
            const parsed_description = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/description]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["description"]);
            })();
            if (!parsed_description.success) {
              return { success: false, reason: parsed_description.reason };
            }
            if (!hasProp(value, "language")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/language]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'language'.` };
            }
            const parsed_language = ((): ParserResult<Language> => {
              return ((value: unknown): ParserResult<Language> => {
                return deserialize_Language_internal(value);
              })(value["language"]);
            })();
            if (!parsed_language.success) {
              return { success: false, reason: parsed_language.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true>> => {
              return ((value: unknown): ParserResult<DateTime<true>> => {
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/modifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/modifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            if (!hasProp(value, "name")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/name]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'name'.` };
            }
            const parsed_name = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/name]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["name"]);
            })();
            if (!parsed_name.success) {
              return { success: false, reason: parsed_name.reason };
            }
            if (!hasProp(value, "title")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/title]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'title'.` };
            }
            const parsed_title = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ACTIONABLE/title]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["title"]);
            })();
            if (!parsed_title.success) {
              return { success: false, reason: parsed_title.reason };
            }
            return {
              success: true,
              value: {
                actionableId: parsed_actionableId.value,
                actionableSetId: parsed_actionableSetId.value,
                activatedAt: parsed_activatedAt.value,
                active: parsed_active.value,
                common: parsed_common.value,
                contentId: parsed_contentId.value,
                coverUri: parsed_coverUri.value,
                description: parsed_description.value,
                language: parsed_language.value,
                modifiedAt: parsed_modifiedAt.value,
                name: parsed_name.value,
                title: parsed_title.value,
              },
            };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "ACTIONABLE" as const, ...parsedVariant.value } };
        }
        case "MIAB": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            common: ContentMetadataCommon;
            contentId: ContentId_MIAB;
            customerId: number | null;
            dataId: number;
            modifiedAt: DateTime<true> | null;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_MIAB> => {
              return ((value: unknown): ParserResult<ContentId_MIAB> => {
                return deserialize_ContentId_MIAB_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "customerId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/customerId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'customerId'.` };
            }
            const parsed_customerId = ((): ParserResult<number | null> => {
              return ((value: unknown): ParserResult<number | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/customerId]: '${value}' expected to be of type number.` };
              })(value["customerId"]);
            })();
            if (!parsed_customerId.success) {
              return { success: false, reason: parsed_customerId.reason };
            }
            if (!hasProp(value, "dataId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/dataId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'dataId'.` };
            }
            const parsed_dataId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/dataId]: '${value}' expected to be of type number.` };
              })(value["dataId"]);
            })();
            if (!parsed_dataId.success) {
              return { success: false, reason: parsed_dataId.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/modifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/MIAB/modifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            return { success: true, value: { common: parsed_common.value, contentId: parsed_contentId.value, customerId: parsed_customerId.value, dataId: parsed_dataId.value, modifiedAt: parsed_modifiedAt.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "MIAB" as const, ...parsedVariant.value } };
        }
        case "ONLINE_CONTENT": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            authorsDetail: {
              editorialTeam: boolean;
              gender: OnlineContentCreatorGender;
              name: string;
              rating: number | null;
            }[];
            common: ContentMetadataCommon;
            contentId: ContentId_ONLINE_CONTENT;
            contentProductionResourceId: bigint | null;
            contentRating: number | null;
            deactivatedAt: DateTime<true> | null;
            formatType: OnlineContentFormatType;
            hashId: string;
            modifiedAt: DateTime<true> | null;
            overallRating: number | null;
            publicationDate: { year: number; month: number; day: number };
            readingTime: number;
            resourceUrl: string;
            sourceDetail: {
              logoUri: string | null;
              rating: number | null;
              title: string;
              url: string;
            };
            subtitle: string | null;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "authorsDetail")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'authorsDetail'.` };
            }
            const parsed_authorsDetail = ((): ParserResult<
              {
                editorialTeam: boolean;
                gender: OnlineContentCreatorGender;
                name: string;
                rating: number | null;
              }[]
            > => {
              return ((
                value: unknown,
              ): ParserResult<
                {
                  editorialTeam: boolean;
                  gender: OnlineContentCreatorGender;
                  name: string;
                  rating: number | null;
                }[]
              > => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail]: '${value}' expected to be an array.` };
                }
                const parsed = value.map(
                  (
                    value: unknown,
                  ): ParserResult<{
                    editorialTeam: boolean;
                    gender: OnlineContentCreatorGender;
                    name: string;
                    rating: number | null;
                  }> => {
                    if (typeof value !== "object") {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]]: '${value}' expected to be of type 'object'.` };
                    }
                    if (value === null) {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]]: '${value}' expected to not be null.` };
                    }
                    if (!hasProp(value, "editorialTeam")) {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/editorialTeam]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'editorialTeam'.` };
                    }
                    const parsed_editorialTeam = ((): ParserResult<boolean> => {
                      return ((value: unknown): ParserResult<boolean> => {
                        if (typeof value !== "boolean") {
                          return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/editorialTeam]: '${value}' expected to be of type boolean.` };
                        }
                        return { success: true, value: value };
                      })(value["editorialTeam"]);
                    })();
                    if (!parsed_editorialTeam.success) {
                      return { success: false, reason: parsed_editorialTeam.reason };
                    }
                    if (!hasProp(value, "gender")) {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/gender]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'gender'.` };
                    }
                    const parsed_gender = ((): ParserResult<OnlineContentCreatorGender> => {
                      return ((value: unknown): ParserResult<OnlineContentCreatorGender> => {
                        return deserialize_OnlineContentCreatorGender_internal(value);
                      })(value["gender"]);
                    })();
                    if (!parsed_gender.success) {
                      return { success: false, reason: parsed_gender.reason };
                    }
                    if (!hasProp(value, "name")) {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/name]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'name'.` };
                    }
                    const parsed_name = ((): ParserResult<string> => {
                      return ((value: unknown): ParserResult<string> => {
                        if (typeof value !== "string") {
                          return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/name]: '${value}' expected to be of type string.` };
                        }
                        return { success: true, value: value };
                      })(value["name"]);
                    })();
                    if (!parsed_name.success) {
                      return { success: false, reason: parsed_name.reason };
                    }
                    if (!hasProp(value, "rating")) {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/rating]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'rating'.` };
                    }
                    const parsed_rating = ((): ParserResult<number | null> => {
                      return ((value: unknown): ParserResult<number | null> => {
                        if (value === null) {
                          return { success: true, value: null };
                        }
                        if (typeof value === "number") {
                          return { success: true, value: value };
                        }
                        return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail/[object]/rating]: '${value}' expected to be of type number.` };
                      })(value["rating"]);
                    })();
                    if (!parsed_rating.success) {
                      return { success: false, reason: parsed_rating.reason };
                    }
                    return { success: true, value: { editorialTeam: parsed_editorialTeam.value, gender: parsed_gender.value, name: parsed_name.value, rating: parsed_rating.value } };
                  },
                );
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/authorsDetail]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["authorsDetail"]);
            })();
            if (!parsed_authorsDetail.success) {
              return { success: false, reason: parsed_authorsDetail.reason };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_ONLINE_CONTENT> => {
              return ((value: unknown): ParserResult<ContentId_ONLINE_CONTENT> => {
                return deserialize_ContentId_ONLINE_CONTENT_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "contentProductionResourceId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/contentProductionResourceId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentProductionResourceId'.` };
            }
            const parsed_contentProductionResourceId = ((): ParserResult<bigint | null> => {
              return ((value: unknown): ParserResult<bigint | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: BigInt(value) };
                }
                if (typeof value === "bigint") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/contentProductionResourceId]: '${value}' expected to be of type bigint or number.` };
              })(value["contentProductionResourceId"]);
            })();
            if (!parsed_contentProductionResourceId.success) {
              return { success: false, reason: parsed_contentProductionResourceId.reason };
            }
            if (!hasProp(value, "contentRating")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/contentRating]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentRating'.` };
            }
            const parsed_contentRating = ((): ParserResult<number | null> => {
              return ((value: unknown): ParserResult<number | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/contentRating]: '${value}' expected to be of type number.` };
              })(value["contentRating"]);
            })();
            if (!parsed_contentRating.success) {
              return { success: false, reason: parsed_contentRating.reason };
            }
            if (!hasProp(value, "deactivatedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/deactivatedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'deactivatedAt'.` };
            }
            const parsed_deactivatedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/deactivatedAt]: '${value}' expected to be of type string.` };
                }
                const parsed = DateTime.fromISO(value.toUpperCase(), { setZone: true });
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/deactivatedAt]: '${value}' expected to be a valid ISO date.` };
                }
                return { success: true, value: parsed };
              })(value["deactivatedAt"]);
            })();
            if (!parsed_deactivatedAt.success) {
              return { success: false, reason: parsed_deactivatedAt.reason };
            }
            if (!hasProp(value, "formatType")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/formatType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'formatType'.` };
            }
            const parsed_formatType = ((): ParserResult<OnlineContentFormatType> => {
              return ((value: unknown): ParserResult<OnlineContentFormatType> => {
                return deserialize_OnlineContentFormatType_internal(value);
              })(value["formatType"]);
            })();
            if (!parsed_formatType.success) {
              return { success: false, reason: parsed_formatType.reason };
            }
            if (!hasProp(value, "hashId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/hashId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'hashId'.` };
            }
            const parsed_hashId = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/hashId]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["hashId"]);
            })();
            if (!parsed_hashId.success) {
              return { success: false, reason: parsed_hashId.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true> | null> => {
              return ((value: unknown): ParserResult<DateTime<true> | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/modifiedAt]: '${value}' expected to be of type string.` };
                }
                const parsed = DateTime.fromISO(value.toUpperCase(), { setZone: true });
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/modifiedAt]: '${value}' expected to be a valid ISO date.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            if (!hasProp(value, "overallRating")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/overallRating]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'overallRating'.` };
            }
            const parsed_overallRating = ((): ParserResult<number | null> => {
              return ((value: unknown): ParserResult<number | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/overallRating]: '${value}' expected to be of type number.` };
              })(value["overallRating"]);
            })();
            if (!parsed_overallRating.success) {
              return { success: false, reason: parsed_overallRating.reason };
            }
            if (!hasProp(value, "publicationDate")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/publicationDate]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'publicationDate'.` };
            }
            const parsed_publicationDate = ((): ParserResult<{ year: number; month: number; day: number }> => {
              return ((value: unknown): ParserResult<{ year: number; month: number; day: number }> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/publicationDate]: '${value}' expected to be of type string.` };
                }
                const parsed = DateTime.fromFormat(value, "yyyy-LL-dd");
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/publicationDate]: '${value}' expected to be a valid date in format yyyy-LL-dd.` };
                }
                return { success: true, value: { year: parsed.year, month: parsed.month, day: parsed.day } };
              })(value["publicationDate"]);
            })();
            if (!parsed_publicationDate.success) {
              return { success: false, reason: parsed_publicationDate.reason };
            }
            if (!hasProp(value, "readingTime")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/readingTime]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'readingTime'.` };
            }
            const parsed_readingTime = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/readingTime]: '${value}' expected to be of type number.` };
              })(value["readingTime"]);
            })();
            if (!parsed_readingTime.success) {
              return { success: false, reason: parsed_readingTime.reason };
            }
            if (!hasProp(value, "resourceUrl")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/resourceUrl]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'resourceUrl'.` };
            }
            const parsed_resourceUrl = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/resourceUrl]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["resourceUrl"]);
            })();
            if (!parsed_resourceUrl.success) {
              return { success: false, reason: parsed_resourceUrl.reason };
            }
            if (!hasProp(value, "sourceDetail")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'sourceDetail'.` };
            }
            const parsed_sourceDetail = ((): ParserResult<{
              logoUri: string | null;
              rating: number | null;
              title: string;
              url: string;
            }> => {
              return ((
                value: unknown,
              ): ParserResult<{
                logoUri: string | null;
                rating: number | null;
                title: string;
                url: string;
              }> => {
                if (typeof value !== "object") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail]: '${value}' expected to be of type 'object'.` };
                }
                if (value === null) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail]: '${value}' expected to not be null.` };
                }
                if (!hasProp(value, "logoUri")) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/logoUri]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'logoUri'.` };
                }
                const parsed_logoUri = ((): ParserResult<string | null> => {
                  return ((value: unknown): ParserResult<string | null> => {
                    if (value === null) {
                      return { success: true, value: null };
                    }
                    if (typeof value !== "string") {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/logoUri]: '${value}' expected to be of type string.` };
                    }
                    return { success: true, value: value };
                  })(value["logoUri"]);
                })();
                if (!parsed_logoUri.success) {
                  return { success: false, reason: parsed_logoUri.reason };
                }
                if (!hasProp(value, "rating")) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/rating]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'rating'.` };
                }
                const parsed_rating = ((): ParserResult<number | null> => {
                  return ((value: unknown): ParserResult<number | null> => {
                    if (value === null) {
                      return { success: true, value: null };
                    }
                    if (typeof value === "number") {
                      return { success: true, value: value };
                    }
                    return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/rating]: '${value}' expected to be of type number.` };
                  })(value["rating"]);
                })();
                if (!parsed_rating.success) {
                  return { success: false, reason: parsed_rating.reason };
                }
                if (!hasProp(value, "title")) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/title]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'title'.` };
                }
                const parsed_title = ((): ParserResult<string> => {
                  return ((value: unknown): ParserResult<string> => {
                    if (typeof value !== "string") {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/title]: '${value}' expected to be of type string.` };
                    }
                    return { success: true, value: value };
                  })(value["title"]);
                })();
                if (!parsed_title.success) {
                  return { success: false, reason: parsed_title.reason };
                }
                if (!hasProp(value, "url")) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/url]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'url'.` };
                }
                const parsed_url = ((): ParserResult<string> => {
                  return ((value: unknown): ParserResult<string> => {
                    if (typeof value !== "string") {
                      return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/sourceDetail/url]: '${value}' expected to be of type string.` };
                    }
                    return { success: true, value: value };
                  })(value["url"]);
                })();
                if (!parsed_url.success) {
                  return { success: false, reason: parsed_url.reason };
                }
                return { success: true, value: { logoUri: parsed_logoUri.value, rating: parsed_rating.value, title: parsed_title.value, url: parsed_url.value } };
              })(value["sourceDetail"]);
            })();
            if (!parsed_sourceDetail.success) {
              return { success: false, reason: parsed_sourceDetail.reason };
            }
            if (!hasProp(value, "subtitle")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/subtitle]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'subtitle'.` };
            }
            const parsed_subtitle = ((): ParserResult<string | null> => {
              return ((value: unknown): ParserResult<string | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/ONLINE_CONTENT/subtitle]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["subtitle"]);
            })();
            if (!parsed_subtitle.success) {
              return { success: false, reason: parsed_subtitle.reason };
            }
            return {
              success: true,
              value: {
                authorsDetail: parsed_authorsDetail.value,
                common: parsed_common.value,
                contentId: parsed_contentId.value,
                contentProductionResourceId: parsed_contentProductionResourceId.value,
                contentRating: parsed_contentRating.value,
                deactivatedAt: parsed_deactivatedAt.value,
                formatType: parsed_formatType.value,
                hashId: parsed_hashId.value,
                modifiedAt: parsed_modifiedAt.value,
                overallRating: parsed_overallRating.value,
                publicationDate: parsed_publicationDate.value,
                readingTime: parsed_readingTime.value,
                resourceUrl: parsed_resourceUrl.value,
                sourceDetail: parsed_sourceDetail.value,
                subtitle: parsed_subtitle.value,
              },
            };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "ONLINE_CONTENT" as const, ...parsedVariant.value } };
        }
        case "EXPERT_VIDEO": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            authors: AuthorListView[];
            common: ContentMetadataCommon;
            contentId: ContentId_EXPERT_VIDEO;
            length: number | null;
            modifiedAt: DateTime<true>;
            whatYouWillLearn: string;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "authors")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/authors]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'authors'.` };
            }
            const parsed_authors = ((): ParserResult<AuthorListView[]> => {
              return ((value: unknown): ParserResult<AuthorListView[]> => {
                if (!isArray(value)) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/authors]: '${value}' expected to be an array.` };
                }
                const parsed = value.map((value: unknown): ParserResult<AuthorListView> => {
                  return deserialize_AuthorListView_internal(value);
                });
                if (parsed.find((value) => !value.success) !== undefined) {
                  return {
                    success: false,
                    reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/authors]: Reason(s): '${parsed
                      .filter((p) => !p.success)
                      .map((p) => p.reason)
                      .join(", ")}'.`,
                  };
                }
                return {
                  success: true,
                  value: parsed.map((entry) => {
                    assert(entry.success == true);
                    return entry.value;
                  }),
                };
              })(value["authors"]);
            })();
            if (!parsed_authors.success) {
              return { success: false, reason: parsed_authors.reason };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_EXPERT_VIDEO> => {
              return ((value: unknown): ParserResult<ContentId_EXPERT_VIDEO> => {
                return deserialize_ContentId_EXPERT_VIDEO_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "length")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/length]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'length'.` };
            }
            const parsed_length = ((): ParserResult<number | null> => {
              return ((value: unknown): ParserResult<number | null> => {
                if (value === null) {
                  return { success: true, value: null };
                }
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/length]: '${value}' expected to be of type number.` };
              })(value["length"]);
            })();
            if (!parsed_length.success) {
              return { success: false, reason: parsed_length.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true>> => {
              return ((value: unknown): ParserResult<DateTime<true>> => {
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/modifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/modifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            if (!hasProp(value, "whatYouWillLearn")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/whatYouWillLearn]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'whatYouWillLearn'.` };
            }
            const parsed_whatYouWillLearn = ((): ParserResult<string> => {
              return ((value: unknown): ParserResult<string> => {
                if (typeof value !== "string") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/EXPERT_VIDEO/whatYouWillLearn]: '${value}' expected to be of type string.` };
                }
                return { success: true, value: value };
              })(value["whatYouWillLearn"]);
            })();
            if (!parsed_whatYouWillLearn.success) {
              return { success: false, reason: parsed_whatYouWillLearn.reason };
            }
            return {
              success: true,
              value: { authors: parsed_authors.value, common: parsed_common.value, contentId: parsed_contentId.value, length: parsed_length.value, modifiedAt: parsed_modifiedAt.value, whatYouWillLearn: parsed_whatYouWillLearn.value },
            };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "EXPERT_VIDEO" as const, ...parsedVariant.value } };
        }
        case "QUICKWIN": {
          const parsedVariant = ((
            value: unknown,
          ): ParserResult<{
            common: ContentMetadataCommon;
            contentId: ContentId_QUICKWIN;
            dataId: number;
            modifiedAt: DateTime<true>;
            quickWinType: QuickWinType;
          }> => {
            if (typeof value !== "object") {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN]: '${value}' expected to be of type 'object'.` };
            }
            if (value === null) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN]: '${value}' expected to not be null.` };
            }
            if (!hasProp(value, "common")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/common]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'common'.` };
            }
            const parsed_common = ((): ParserResult<ContentMetadataCommon> => {
              return ((value: unknown): ParserResult<ContentMetadataCommon> => {
                return deserialize_ContentMetadataCommon_internal(value);
              })(value["common"]);
            })();
            if (!parsed_common.success) {
              return { success: false, reason: parsed_common.reason };
            }
            if (!hasProp(value, "contentId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/contentId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'contentId'.` };
            }
            const parsed_contentId = ((): ParserResult<ContentId_QUICKWIN> => {
              return ((value: unknown): ParserResult<ContentId_QUICKWIN> => {
                return deserialize_ContentId_QUICKWIN_internal(value);
              })(value["contentId"]);
            })();
            if (!parsed_contentId.success) {
              return { success: false, reason: parsed_contentId.reason };
            }
            if (!hasProp(value, "dataId")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/dataId]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'dataId'.` };
            }
            const parsed_dataId = ((): ParserResult<number> => {
              return ((value: unknown): ParserResult<number> => {
                if (typeof value === "number") {
                  return { success: true, value: value };
                }
                return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/dataId]: '${value}' expected to be of type number.` };
              })(value["dataId"]);
            })();
            if (!parsed_dataId.success) {
              return { success: false, reason: parsed_dataId.reason };
            }
            if (!hasProp(value, "modifiedAt")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/modifiedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'modifiedAt'.` };
            }
            const parsed_modifiedAt = ((): ParserResult<DateTime<true>> => {
              return ((value: unknown): ParserResult<DateTime<true>> => {
                if (typeof value !== "number") {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/modifiedAt]: '${value}' expected to be of type number.` };
                }
                const parsed = DateTime.fromMillis(value);
                if (!parsed.isValid) {
                  return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/modifiedAt]: '${value}' expected to be valid DateTime.` };
                }
                return { success: true, value: parsed };
              })(value["modifiedAt"]);
            })();
            if (!parsed_modifiedAt.success) {
              return { success: false, reason: parsed_modifiedAt.reason };
            }
            if (!hasProp(value, "quickWinType")) {
              return { success: false, reason: `[#/components/schemas/ContentMetadata/QUICKWIN/quickWinType]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'quickWinType'.` };
            }
            const parsed_quickWinType = ((): ParserResult<QuickWinType> => {
              return ((value: unknown): ParserResult<QuickWinType> => {
                return deserialize_QuickWinType_internal(value);
              })(value["quickWinType"]);
            })();
            if (!parsed_quickWinType.success) {
              return { success: false, reason: parsed_quickWinType.reason };
            }
            return { success: true, value: { common: parsed_common.value, contentId: parsed_contentId.value, dataId: parsed_dataId.value, modifiedAt: parsed_modifiedAt.value, quickWinType: parsed_quickWinType.value } };
          })(value);
          if (!parsedVariant.success) {
            return { success: false, reason: parsedVariant.reason };
          }
          return { success: true, value: { contentType: "QUICKWIN" as const, ...parsedVariant.value } };
        }
        default:
          return { success: false, reason: `[#/components/schemas/ContentMetadata]: '${discriminator}' expected to be one of 'SUMMARY, ACTIONABLE, MIAB, ONLINE_CONTENT, EXPERT_VIDEO, QUICKWIN'.` };
      }
    })(value);
  })(value);
}
export function deserialize_ContentMetadata(str: string): ParserResult<ContentMetadata> {
  return deserialize_ContentMetadata_internal(parseJsonWithNumbersAsBigint(str));
}
